import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { LogOut, CreditCard, Wallet, Building2 as Landmark, Menu, X, Sun, Moon, Search, ChevronRight, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { clearToken, getCurrentUser, getPsp, setPsp } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { BatLogo } from '@/components/BatLogo';
import { useGothamTheme } from '@/lib/useGothamTheme';
import { AdminNav } from '@/components/admin/AdminNav';
import { AdminSearch } from '@/components/admin/AdminSearch';
import { AdminPaletteSelector } from '@/components/admin/AdminPaletteSelector';
import { platforms, itemForPath, visibleFor, navGroups, platformItems } from '@/components/admin/navigation';
import { basePath, loginFor, hasPerm } from '@/lib/base';
import { Navigate } from 'react-router-dom';
import '@/styles/admin.css';

const EXTRA_PAGES = [{ to: '/subscriptions', perm: 'billing' }, { to: '/tutoriels' }];
const PSP_PERMS = ['transactions', 'analytics', 'billing', 'stripe_accounts', 'viva_accounts', 'payzen_accounts', 'recovery', 'vitrines'];

export default function AdminLayout() {
  const nav = useNavigate();
  const location = useLocation();
  const me = getCurrentUser();
  const base = basePath();
  const { theme, toggle, palette, setPalette } = useGothamTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTriggerRef = useRef(null);
  const [mobile, setMobile] = useState(() => !window.matchMedia('(min-width: 1024px)').matches);
  const [psp, setPspState] = useState(getPsp());
  const [alertCount, setAlertCount] = useState(0);
  const [defaultPin, setDefaultPin] = useState(false);
  useEffect(() => {
    if (me?.role !== 'super_admin') return undefined;
    const poll = () => api.get('/security/alerts', { params: { limit: 1 } }).then((r) => setAlertCount(r.data.unread || 0)).catch(() => {});
    poll(); api.get('/auth/security-status').then((r) => setDefaultPin(!!r.data.default_pin)).catch(() => {});
    const t = setInterval(poll, 60000);
    window.addEventListener('oracle-alerts-seen', poll);
    return () => { clearInterval(t); window.removeEventListener('oracle-alerts-seen', poll); };
  }, [me?.role]);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const change = () => { setMobile(!mq.matches); if (mq.matches) setDrawerOpen(false); };
    mq.addEventListener('change', change);
    return () => mq.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    const shortcut = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchOpen((v) => !v); } };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, []);
  const switchPsp = (p) => {
    if (p === psp) return;
    setPsp(p); setPspState(p); nav(base);
  };
  const logout = async () => { try { await api.post('/auth/logout'); } catch { /* token may already be dead */ } clearToken(); nav(loginFor(me), { replace: true }); };
  const currentItem = itemForPath(base, location.pathname) || EXTRA_PAGES.find((i) => location.pathname === `${base}${i.to}`);
  const firstAllowed = [...navGroups.flatMap((g) => g.items), ...platformItems('')].find((i) => visibleFor(i, me, psp) && i.to !== '');
  const fallback = firstAllowed ? `${base}${firstAllowed.to}` : `${base}/tutoriels`;
  const pageAllowed = (item) => (!item.roles || item.roles.includes(me?.role)) && hasPerm(me, item.perm);
  if (currentItem && !pageAllowed(currentItem)) return <Navigate to={currentItem.to === '/payouts' && me?.role === 'super_admin' ? `${base}/stripe-monitor` : fallback} replace />;
  if (location.pathname === base && me?.role === 'collaborator' && !hasPerm(me, 'transactions')) return <Navigate to={fallback} replace />;
  const showPsp = me?.role !== 'collaborator' || PSP_PERMS.some((k) => hasPerm(me, k));
  const platform = platforms.find((p) => p.key === new URLSearchParams(location.search).get('platform'));
  const sidebar = <>
    <div className="admin-brand-row">
      <Link to={base} className="admin-brand" data-testid="admin-brand-link">
        <span className="admin-brand-mark"><BatLogo className="w-6 h-6" /></span>
        <span><span className="admin-brand-name">Gotham<span className="admin-brand-sub">Checkout</span></span></span>
      </Link>
      {mobile && <DialogPrimitive.Close className="admin-icon-button" aria-label="Fermer le menu" data-testid="admin-mobile-close"><X className="h-4 w-4" /></DialogPrimitive.Close>}
    </div>
    {showPsp && <div className="admin-processor" data-testid="psp-switcher" role="group" aria-label="Prestataire de paiement">
      {[{ key: 'stripe', label: 'Stripe', icon: CreditCard }, { key: 'viva', label: 'Viva', icon: Wallet }, { key: 'payzen', label: 'PayZen', icon: Landmark }].map((p) => (
        <button key={p.key} type="button" onClick={() => switchPsp(p.key)} data-testid={`psp-switch-${p.key}`} aria-pressed={psp === p.key} className={`admin-processor-option ${psp === p.key ? 'is-active' : ''}`}>
          <p.icon className="w-3.5 h-3.5" />{p.label}
        </button>
      ))}
    </div>}
    <AdminNav user={me} psp={psp} onNavigate={() => setDrawerOpen(false)} badges={{ '/oracle': alertCount }} />
    <div className="admin-sidebar-footer">
      <div className="admin-profile">
        <span className="admin-avatar" aria-hidden="true">{(me?.name || 'A').slice(0, 1).toUpperCase()}</span>
        <div className="min-w-0 flex-1"><div className="truncate text-xs font-semibold" data-testid="admin-current-user-name">{me?.name || 'Administrateur'}</div><div className="admin-profile-role" data-testid="admin-current-user-role">{{ super_admin: 'Super administrateur', collaborator: 'Collaborateur', manager: 'Manager', viewer: 'Lecture seule' }[me?.role] || me?.role}</div></div>
        <button onClick={logout} data-testid={ADMIN.logoutButton} className="admin-icon-button" title="Déconnexion" aria-label="Déconnexion"><LogOut className="h-4 w-4" /></button>
      </div>
      <div className="admin-signature">Wayne Enterprises</div>
    </div>
  </>;
  return <div className="admin-shell" data-testid="admin-shell">
    <a href="#admin-main" className="admin-skip-link" data-testid="admin-skip-link">Aller au contenu</a>
    {mobile ? <DialogPrimitive.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
      <DialogPrimitive.Trigger asChild><button className="admin-mobile-trigger admin-icon-button" data-testid="admin-mobile-menu-button" aria-label="Ouvrir le menu"><Menu className="h-5 w-5" /></button></DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="admin-drawer-overlay" data-testid="admin-mobile-backdrop" />
        <DialogPrimitive.Content className="admin-sidebar admin-sidebar-mobile" data-testid="admin-sidebar" aria-describedby={undefined}>
          <DialogPrimitive.Title className="sr-only">Menu Gotham Checkout</DialogPrimitive.Title>{sidebar}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root> : <aside className="admin-sidebar" data-testid="admin-sidebar">{sidebar}</aside>}
    <div className="admin-workspace">
      <header className="admin-topbar" data-testid="admin-topbar">
        <div className="admin-breadcrumb" data-testid="admin-breadcrumb"><span>{mobile ? 'Gotham' : platform?.label || 'Espace de travail'}</span><ChevronRight aria-hidden="true" /><strong>{currentItem?.label || 'Administration'}</strong></div>
        <div className="flex items-center gap-2">
          <button ref={searchTriggerRef} onClick={() => setSearchOpen(true)} className="admin-search-trigger" data-testid="admin-search-trigger" aria-label="Rechercher une page" title="Rechercher une page"><Search className="h-4 w-4" /><span>Rechercher</span></button>
          <span className="admin-topbar-divider" />
          <AdminPaletteSelector palette={palette} onChange={setPalette} />
          <button onClick={toggle} className="admin-icon-button" data-testid={mobile ? 'admin-theme-toggle-mobile' : 'admin-theme-toggle'} aria-label={theme === 'dark' ? 'Activer le thème clair' : 'Activer le thème sombre'} title={theme === 'dark' ? 'Thème clair' : 'Thème sombre'}>{theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>
        </div>
      </header>
      {defaultPin && <div className="mx-4 mt-4 sm:mx-6 lg:mx-10 flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="default-pin-banner"><ShieldAlert className="h-4 w-4 shrink-0" /><span className="flex-1"><strong>PIN par défaut détecté (000000).</strong> N'importe qui connaissant cette valeur peut prendre le contrôle. Changez-le maintenant.</span><Link to={`${base}/users`} className="font-semibold underline underline-offset-2" data-testid="default-pin-banner-link">Changer mon PIN</Link></div>}
      <main id="admin-main" tabIndex={-1} className="admin-main" data-testid="admin-main"><Outlet key={psp} /></main>
    </div>
    <AdminSearch open={searchOpen} onOpenChange={setSearchOpen} user={me} psp={psp} triggerRef={searchTriggerRef} />
  </div>;
}